// Browser-safe PIN utilities for the M1 lock flow.
// PBKDF2-SHA256 100k iter (PLAN 13-3장). Uses the Web Crypto API, so all
// operations are asynchronous.

const PBKDF2_ITERATIONS = 100_000;
const KEY_LENGTH = 32;

const subtle = window.crypto.subtle;

function toBufferSource(bytes: Uint8Array): BufferSource {
  return bytes as unknown as BufferSource;
}

export function generateSalt(): string {
  const buf = new Uint8Array(16);
  window.crypto.getRandomValues(buf);
  return base64url(buf);
}

export async function hashPin(pin: string, salt: string): Promise<string> {
  const saltBytes = toBufferSource(base64urlToBytes(salt));
  const pinBytes = toBufferSource(new TextEncoder().encode(pin));

  const key = await subtle.importKey(
    'raw',
    pinBytes,
    { name: 'PBKDF2', length: KEY_LENGTH * 8 },
    false,
    ['deriveBits'],
  );
  const bits = await subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: saltBytes,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    key,
    KEY_LENGTH * 8,
  );
  return base64url(new Uint8Array(bits));
}

export async function verifyPin(
  pin: string,
  pinSetting: { hash: string; salt: string },
): Promise<boolean> {
  const inputHash = await hashPin(pin, pinSetting.salt);

  const a = base64urlToBytes(inputHash);
  const b = base64urlToBytes(pinSetting.hash);
  if (a.length !== b.length) return false;

  let result = 0;
  for (let i = 0; i < a.length; i++) {
    result |= (a[i] ?? 0) ^ (b[i] ?? 0);
  }
  return result === 0;
}

function base64url(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i] ?? 0);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64urlToBytes(str: string): Uint8Array {
  str = str.replace(/-/g, '+').replace(/_/g, '/');
  switch (str.length % 4) {
    case 0:
      break;
    case 2:
      str += '==';
      break;
    case 3:
      str += '=';
      break;
    default:
      throw new Error('Invalid base64url length');
  }
  const binary = atob(str);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}
